import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Square, Wind } from "lucide-react";
import { useLang } from "../i18n";
import { rhythmLabel } from "../constants/breath";

// Dynamic breathing timer. Reads the rhythm from `pattern` ({inhale,hold1,exhale,hold2})
// or legacy inhale/exhale props. The circle expands on inhale, holds, contracts on exhale.
export function BreathTimer({ color = "#86A6B8", inhale = 5, exhale = 5, pattern = null, compact = false }) {
  const { t } = useLang();
  const pat = pattern || { inhale, hold1: 0, exhale, hold2: 0 };

  const phases = useMemo(() => {
    const out = [];
    let big = false;
    if (pat.inhale > 0) { big = true; out.push({ key: "inhale", secs: pat.inhale, big }); }
    if (pat.hold1 > 0) out.push({ key: "hold", secs: pat.hold1, big });
    if (pat.exhale > 0) { big = false; out.push({ key: "exhale", secs: pat.exhale, big }); }
    if (pat.hold2 > 0) out.push({ key: "hold", secs: pat.hold2, big });
    return out.length ? out : [{ key: "inhale", secs: 5, big: true }, { key: "exhale", secs: 5, big: false }];
  }, [pat.inhale, pat.hold1, pat.exhale, pat.hold2]);

  const [running, setRunning] = useState(false);
  const [idx, setIdx] = useState(0);
  const [cycles, setCycles] = useState(0);
  const ref = useRef(null);

  useEffect(() => () => clearTimeout(ref.current), []);

  const schedule = (i) => {
    const ph = phases[i];
    ref.current = setTimeout(() => {
      const next = (i + 1) % phases.length;
      if (next === 0) setCycles((c) => c + 1);
      setIdx(next);
      schedule(next);
    }, ph.secs * 1000);
  };

  const start = () => {
    clearTimeout(ref.current);
    setRunning(true);
    setCycles(0);
    setIdx(0);
    schedule(0);
  };
  const stop = () => { clearTimeout(ref.current); setRunning(false); setIdx(0); };

  const cur = phases[idx] || phases[0];
  const BIG = compact ? 150 : 224;
  const SMALL = compact ? 78 : 110;
  const IDLE = compact ? 104 : 150;
  const box = compact ? "w-40 h-40" : "w-56 h-56";
  const size = running ? (cur.big ? BIG : SMALL) : IDLE;
  const labelKey = cur.key === "inhale" ? "breath_inhale" : cur.key === "exhale" ? "breath_exhale" : "breath_hold";

  return (
    <div className="flex flex-col items-center gap-4 py-2" data-testid="breath-timer">
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider" style={{ color }}>
        <Wind className="w-3.5 h-3.5" /> {t("breath_rhythm")}: {rhythmLabel(pat)}
      </span>
      <div className={`relative ${box} flex items-center justify-center`}>
        <motion.div
          className="absolute rounded-full"
          style={{ backgroundColor: `${color}33`, border: `3px solid ${color}` }}
          animate={{ width: size, height: size }}
          transition={{ duration: running ? cur.secs : 0.6, ease: "easeInOut" }}
        />
        <span className={`relative font-serif ${compact ? "text-base" : "text-xl"} z-10`} style={{ color: "#1A1D20" }} data-testid="breath-phase">
          {running ? `${t(labelKey)} · ${cur.secs}s` : t("breath_ready")}
        </span>
      </div>
      {running && <p className="text-sm text-[#6E6B65]" data-testid="breath-cycles">{t("breath_cycles")}: {cycles}</p>}
      {!running ? (
        <button onClick={start} data-testid="breath-start"
          className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold transition-transform hover:scale-105" style={{ backgroundColor: color }}>
          <Play className="w-4 h-4" /> {t("breath_start")}
        </button>
      ) : (
        <button onClick={stop} data-testid="breath-stop"
          className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-semibold border-2 transition-transform hover:scale-105" style={{ borderColor: color, color }}>
          <Square className="w-4 h-4" /> {t("breath_stop")}
        </button>
      )}
    </div>
  );
}
